import { useMutation, useQuery } from "@tanstack/react-query";

import useAxios from "../common/hooks/useAxios";
import {
  ACTIVATE_CUSTOMER,
  CHANGE_PASSWORD,
  DEACTIVATE_CUSTOMER,
  GET_NIC,
  LIST_CUSTOMERS,
  RE_UPLOAD_NIC,
  SELF_CHANGE_PASSWORD,
  UPDATE_CUSTOMER,
} from "../customerManagement/api/customer-api";
import useApi from "../common/hooks/useApi";
import { useCallback } from "react";

export const CUSTOMER_QUERIES = {
  listCustomers: "listCustomers",
  getNICOfCustomer: "getNICOfCustomer",
};

const useCustomerManagementApi = (enabledApis = []) => {
  const axios = useAxios();

  const {
    data: customers = [],
    isFetching: isCustomersLoading,
    refetch: listCustomers,
  } = useQuery({
    queryKey: [CUSTOMER_QUERIES.listCustomers],
    queryFn: () => axios.get(LIST_CUSTOMERS).then((res) => res.data),
    enabled: enabledApis.includes(CUSTOMER_QUERIES.listCustomers),
    refetchOnMount: "always",
  });

  const { mutateAsync: updateCustomer, isPending: isUpdatingCustomer } =
    useMutation({
      mutationFn: (data) =>
        axios.post(UPDATE_CUSTOMER, data).then((res) => res.data),
    });

  const { mutateAsync: changePassword, isPending: isChangingPassword } =
    useMutation({
      mutationFn: (data) =>
        axios.post(CHANGE_PASSWORD, data).then((res) => res.data),
    });

  const { mutateAsync: selfChangePassword, isPending: isSelfChangingPassword } =
    useMutation({
      mutationFn: (data) =>
        axios.post(SELF_CHANGE_PASSWORD, data).then((res) => res.data),
    });

  const { mutateAsync: reUploadNIC, isPending: isNICUploading } = useMutation({
    mutationFn: (data) =>
      axios
        .post(RE_UPLOAD_NIC, data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => res.data),
  });

  const {
    mutateAsync: toggleCustomerState,
    isPending: isTogglingCustomerState,
  } = useMutation({
    mutationFn: (data) =>
      axios
        .post(
          data.isActive ? ACTIVATE_CUSTOMER : DEACTIVATE_CUSTOMER,
          data.data
        )
        .then((res) => res.data),
  });

  const getNICOfCustomer = useCallback(
    async (id) => {
      return await axios.get(GET_NIC(id)).then((res) => res.data);
    },
    [axios]
  );

  const { trigger: getNICOfCustomerTrigger, isLoading: isFetchingNIC } =
    useApi(getNICOfCustomer);

  return {
    listCustomersApi: {
      execute: listCustomers,
      isLoading: isCustomersLoading,
      data: customers,
    },
    updateCustomerApi: {
      execute: updateCustomer,
      isLoading: isUpdatingCustomer,
    },
    changePasswordApi: {
      execute: changePassword,
      isLoading: isChangingPassword,
    },
    selfChangePasswordApi: {
      execute: selfChangePassword,
      isLoading: isSelfChangingPassword,
    },
    reUploadNICApi: {
      execute: reUploadNIC,
      isLoading: isNICUploading,
    },
    toggleCustomerStateApi: {
      execute: toggleCustomerState,
      isLoading: isTogglingCustomerState,
    },
    getNICOfCustomerApi: {
      execute: getNICOfCustomerTrigger,
      isLoading: isFetchingNIC,
    },
  };
};

export default useCustomerManagementApi;
